import { useCallback, useMemo } from 'react'
import { format } from 'date-fns'
import { TFunction, useTranslation } from 'react-i18next'

import { EmployeeActivityType, type EmployeeActivity, type PaymentProvider } from '../useEmployeeActivityQuery.types'

import { Badge, ButtonText, Flex, Typography } from '@percent/lemonade'
import { HistoryListItem } from '@percent/partner-dashboard/common/components/historyListItem/HistoryListItem'
import { formatMoney } from '@percent/utility'
import { dayJS } from '@percent/partner-dashboard/common/library/utility/date'
import styles from './Activity.module.scss'

export type ActivityProps = Readonly<{
  activity: EmployeeActivity
}>

const openLinkInNewTab = (link: string) => {
  window.open(link, '_blank', 'noopener noreferrer')
}

const formatVolunteeringTime = (timeInMinutes: number, t: TFunction) => {
  if (timeInMinutes <= 0) {
    return `0${t('workplaceGiving.timeFormatting.minutesAbbrev')}`
  }

  const durationTime = dayJS.duration(timeInMinutes, 'minutes')
  const days = durationTime.days() ? `${durationTime.days()}${t('workplaceGiving.timeFormatting.daysAbbrev')}` : ''
  const hours = durationTime.hours() ? `${durationTime.hours()}${t('workplaceGiving.timeFormatting.hoursAbbrev')}` : ''
  const minutes = durationTime.minutes()
    ? `${durationTime.minutes()}${t('workplaceGiving.timeFormatting.minutesAbbrev')}`
    : ''

  return `${days}${(days && hours) || (days && minutes) ? ' ' : ''}${hours}${hours && minutes ? ' ' : ''}${minutes}`
}

export function Activity({ activity }: { activity: EmployeeActivity }) {
  const { date, type, linkUrl, iconUrl, data } = activity
  const { t } = useTranslation()

  const donation = type === EmployeeActivityType.DONATED ? data : null

  // Imported wallet donations arrive as off-platform with no payment method, so the funding
  // source rows are the only signal that the money came out of a wallet. The platform badge
  // still reports what the API says, because the donation really was imported.
  const fundingSources = donation?.fundingSources ?? []
  const hasWalletFunding = fundingSources.length > 0
  const showsPaymentMethod = !!donation && (donation.onPlatform || hasWalletFunding)
  const fundingSourceNames = fundingSources.flatMap(source => source.name ?? [])

  const getPaymentMethod = useCallback(
    (paymentMethod: PaymentProvider | null, fromWallet: boolean) => {
      switch (paymentMethod) {
        case 'paypal':
          return t('workplaceGiving.employeeActivity.paymentMethod.paypal')
        case 'donation_wallet':
          return t('workplaceGiving.employeeActivity.paymentMethod.donationWallet')
        default:
          return fromWallet
            ? t('workplaceGiving.employeeActivity.paymentMethod.donationWallet')
            : t('workplaceGiving.employeeActivity.paymentMethod.card')
      }
    },
    [t]
  )

  const activityDescription = useMemo(() => {
    switch (type) {
      case EmployeeActivityType.OPPORTUNITY_CREATED:
        return t('workplaceGiving.employeeActivity.createdOpportunity')
      case EmployeeActivityType.VOLUNTEERED:
        return `${t('workplaceGiving.employeeActivity.volunteered', { time: formatVolunteeringTime(data.minutes, t) })}
        ${
          data.opportunityName
            ? t('workplaceGiving.employeeActivity.volunteeredOnPlatformWith', { opportunityName: data.opportunityName })
            : ''
        }`
      case EmployeeActivityType.FUNDRAISER_CREATED:
        return t('workplaceGiving.employeeActivity.createdFundraiser')
      case EmployeeActivityType.DONATED:
        return showsPaymentMethod
          ? t('workplaceGiving.employeeActivity.donated', {
              amount: formatMoney(data.amount),
              paymentMethod: getPaymentMethod(data.paymentMethod, hasWalletFunding),
              organisationName: data.organisationName
            })
          : t('workplaceGiving.employeeActivity.donatedOffPlatform', {
              amount: formatMoney(data.amount),
              organisationName: data.organisationName
            })
      case EmployeeActivityType.ACHIEVEMENT_EARNED:
        return t('workplaceGiving.employeeActivity.earnedBadge', { badgeName: data.achievementName })
      default:
        return ''
    }
  }, [data, t, type, getPaymentMethod, hasWalletFunding, showsPaymentMethod])

  return (
    <HistoryListItem>
      <Flex direction="column" gap="0.5rem" mb="1rem" align="flex-start">
        <Flex direction="column" gap="0.25rem">
          <Typography $color="neutral300">{format(new Date(date), 'MMM d, yyyy, hh:mm a')}</Typography>
          <Typography variant="default-bold" $color="neutral300">
            {activityDescription}
          </Typography>
          {fundingSourceNames.length > 0 ? (
            <Typography $color="neutral300">
              {t('workplaceGiving.employeeActivity.fundingSources', {
                fundingSourceNames: fundingSourceNames.join(', ')
              })}
            </Typography>
          ) : null}
          {type === EmployeeActivityType.DONATED ? (
            <Flex gap={2}>
              <Badge variant="default">
                {t(
                  data.onPlatform
                    ? 'workplaceGiving.employeeActivity.onPlatform'
                    : 'workplaceGiving.employeeActivity.offPlatform'
                )}
              </Badge>
              {data.recurrence === 'monthly' ? (
                <Badge variant="informative">{t('workplaceGiving.employeeActivity.monthlyDonation')}</Badge>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
        {linkUrl &&
        (type === EmployeeActivityType.FUNDRAISER_CREATED || type === EmployeeActivityType.OPPORTUNITY_CREATED) ? (
          <ButtonText variant="primary" onClick={() => openLinkInNewTab(linkUrl)}>
            {type === EmployeeActivityType.FUNDRAISER_CREATED
              ? t('workplaceGiving.employeeActivity.viewFundraiser')
              : t('workplaceGiving.employeeActivity.viewOpportunity')}
          </ButtonText>
        ) : null}
        {iconUrl && type === EmployeeActivityType.ACHIEVEMENT_EARNED ? (
          <Flex gap="0.5rem" className={styles.activityBadge}>
            <img className={styles.badgeImg} src={iconUrl} alt={`${data.achievementName} icon`} />
            <Typography $color="gray600">{data.achievementName}</Typography>
          </Flex>
        ) : null}
      </Flex>
    </HistoryListItem>
  )
}
